/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { protocolConceptForWireField, wireFieldNamesFor } from './field-catalog';

/**
 * **What `OpenRtbDriver` builds without being asked** — the ORTB half of *state what the
 * runtime supplies for free* (`MAPPING_EDITOR_UX_GUIDANCE` §15, #1117).
 *
 * The VAST path has had this since #951 · R5: a ghosted *Sent automatically* panel listing
 * what goes out with no row authored, whose per-row Edit promotes a value into an operator's
 * own row. The ORTB body has the opposite gap — the driver builds a long list of fields and
 * **the editor shows none of them**, so an operator reads a three-field body editor as *the
 * body* and adds by hand what we already send. Worse here than on VAST: a hand-added `tmax`
 * does not merely duplicate, it competes with a value resolved from the timeout chain.
 *
 * ## Why this replaces `ORTB_DRIVER_MERGE_BASE`
 *
 * That map lived in the console and answered per **section**: `imp: '*'`, meaning *the driver
 * builds the whole impression*. Measured 2026-08-27, that claim is false — the driver sets no
 * `video.playbackend` at all — and misleading for several more: *the driver sets `tagid`* is
 * not *the driver sets `tagid` correctly for this exchange*. A section-grained answer is what
 * let the body paste discard a partner's `tagid`, `protocols` and `minduration` under the
 * heading *"driver-managed"* (#1187).
 *
 * So: **per field, with a sentence, and checked against the driver's real output.**
 * `libs/drivers/ortb-merge-base.spec.ts` builds requests through `sendRequest` and asserts
 * this declaration is a superset of every path produced — a field the driver builds and
 * nobody declared fails the build, which is the direction that matters. `always` is asserted
 * from the other side: it must appear even for a pod carrying no session at all.
 *
 * ## `from` is operator-facing and is the point
 *
 * A list of paths tells an operator nothing they could act on. *"the pod's ceiling, from the
 * request or the strategy"* tells them where to go and change it, which is the difference
 * between a panel that is read and one that is collapsed. Same rule as #1045: describe the
 * outcome, not the mechanism.
 *
 * **Array indices are collapsed** — `imp.video.mimes`, never `imp[0].video.mimes` — because
 * the driver broadcasts one authored impression across every slot it builds, so the address
 * an operator carries is the pattern. That changes with #918, and this list changes with it.
 */

/** How reliably the driver supplies a field. */
export type OrtbMergeBaseCertainty =
  /** Built on every request, whatever the session carries. */
  | 'always'
  /**
   * Built only when something supplies it — a session field, a configured floor, an
   * inventory context. Absent is a normal outcome and not a defect.
   */
  | 'when-known'
  /**
   * **Forwarded if the publisher sent one, and never invented.**
   *
   * A privacy signal is the case, and the distinction is not pedantry: `#912` established that
   * volunteering the resolver's neutral `1---` asserts a regulatory position for viewers who
   * never took it, while dropping a string the publisher *did* send is the silent loss the
   * pass-through rules exist to end. So we forward, and we never fill in.
   *
   * That makes it the one class where **absence is a gap the operator may want to act on** —
   * their exchange may refuse a request carrying no consent signal — which is why validation
   * still flags these and does not flag the rest. Found 2026-08-27 by replacing the
   * section-grained map: the old one covered `imp`/`site`/`app`/`device` and *not* `regs`, and
   * that turned out to be a real distinction nobody had named.
   */
  | 'forwarded-if-sent';

export interface OrtbMergeBaseField {
  /** Dotted path with array indices collapsed, e.g. `imp.video.mimes`. */
  readonly path: string;
  readonly certainty: OrtbMergeBaseCertainty;
  /** What it is built from, in the operator's terms — this is what the panel renders. */
  readonly from: string;
}

export const ORTB_MERGE_BASE: readonly OrtbMergeBaseField[] = [
  // --- Request envelope -----------------------------------------------------------------
  { path: 'id', certainty: 'always', from: 'A new id for each request' },
  { path: 'at', certainty: 'always', from: 'Second-price auction' },
  { path: 'cur', certainty: 'always', from: 'USD' },
  { path: 'test', certainty: 'always', from: 'Live traffic (0)' },
  {
    path: 'tmax',
    certainty: 'always',
    // Not the Timeout itself, and the difference is the whole of `resolveUpstreamBudget`.
    from: 'Your Timeout, less a reserve for the network and our own assembly',
  },

  // --- Impressions ----------------------------------------------------------------------
  { path: 'imp.id', certainty: 'always', from: 'The slot number within the pod' },
  {
    path: 'imp.tagid',
    // **Not `always`, and not *built*** (Todd, 2026-08-27: *"how can the driver build it? isn't
    // that a publisher id?"*). Two things were wrong here at once.
    //
    // It is **forwarded**, not derived: whatever the caller named as its ad unit is echoed into
    // `imp.tagid`. On the ORTB path that is a clean pass-through — `ext.adUnit || imp.tagid` in,
    // the same value out. On VAST it means an SSP is sent the publisher's own word (`pre-roll`),
    // which is meaningless to a partner that assigned you a placement id. That is exactly why the
    // field is declared `from: 'operator'` in the ORTB catalog and why a paste keeps what the
    // sample carried (#1186): the operator knows which of the two their partner expects.
    //
    // And `always` stopped being true in this same session, when `|| 'default'` was removed — a
    // request with no ad unit now omits the key. The spec did not catch it because its
    // *always* probe supplied an ad unit; it no longer does.
    certainty: 'when-known',
    from: 'The ad unit the caller named, forwarded — not an id we can know for your exchange',
  },
  { path: 'imp.secure', certainty: 'always', from: 'HTTPS creatives required' },
  { path: 'imp.bidfloorcur', certainty: 'always', from: 'USD' },
  {
    path: 'imp.bidfloor',
    certainty: 'when-known',
    from: "This source's floor, bounded by the supply source and system minimums",
  },
  { path: 'imp.video.mimes', certainty: 'always', from: 'The formats this proxy can serve' },
  { path: 'imp.video.protocols', certainty: 'always', from: 'The VAST versions this proxy can serve' },
  {
    path: 'imp.video.minduration',
    certainty: 'always',
    from: "This source's minimum ad length, or 5s — the shortest ad worth requesting",
  },
  {
    path: 'imp.video.maxduration',
    // Always: a pod always states a break length (`PodDto.maxDuration` defaults to the floor).
    certainty: 'always',
    from: "The longest one ad may run: this source's per-ad maximum, clamped by the break's length",
  },
  { path: 'imp.video.w', certainty: 'always', from: "The player's width, from the session" },
  { path: 'imp.video.h', certainty: 'always', from: "The player's height, from the session" },
  { path: 'imp.video.startdelay', certainty: 'always', from: 'Pre-roll (0)' },
  { path: 'imp.video.playbackmethod', certainty: 'always', from: 'Auto-play, sound on or off' },
  { path: 'imp.video.api', certainty: 'always', from: 'VPAID 1/2 and OMID 1' },
  {
    path: 'imp.video.plcmt',
    certainty: 'always',
    from: 'The placement concept, rendered for the version this source speaks',
  },
  {
    path: 'imp.video.linearity',
    certainty: 'always',
    from: 'The linearity concept, rendered for the version this source speaks',
  },

  // --- Device ---------------------------------------------------------------------------
  { path: 'device.w', certainty: 'always', from: "The player's width, from the session" },
  { path: 'device.h', certainty: 'always', from: "The player's height, from the session" },
  { path: 'device.ua', certainty: 'when-known', from: "The viewer's user agent" },
  { path: 'device.ip', certainty: 'when-known', from: "The viewer's IP address" },
  { path: 'device.devicetype', certainty: 'when-known', from: 'The device type, from the session' },
  { path: 'device.make', certainty: 'when-known', from: 'The device make' },
  { path: 'device.model', certainty: 'when-known', from: 'The device model' },
  { path: 'device.os', certainty: 'when-known', from: 'The device operating system' },
  { path: 'device.osv', certainty: 'when-known', from: 'The operating system version' },
  { path: 'device.hwv', certainty: 'when-known', from: 'The hardware version' },
  { path: 'device.ifa', certainty: 'when-known', from: "The viewer's advertising id" },
  { path: 'device.ext.ifa_type', certainty: 'when-known', from: 'The advertising id scheme, sent only alongside one' },
  { path: 'device.lmt', certainty: 'when-known', from: 'Limit ad tracking, from the session' },

  // --- Inventory context ----------------------------------------------------------------
  // `site` and `app` are mutually exclusive per the spec; which one appears follows the
  // session, so neither is `always` even though one of them is.
  { path: 'site.page', certainty: 'when-known', from: 'The page this played on' },
  { path: 'site.domain', certainty: 'when-known', from: 'The publisher domain' },
  { path: 'app.id', certainty: 'when-known', from: 'The app id' },
  { path: 'app.bundle', certainty: 'when-known', from: 'The app bundle' },
  { path: 'app.name', certainty: 'when-known', from: 'The app name' },
  { path: 'app.storeurl', certainty: 'when-known', from: 'The app store URL' },
  { path: 'app.domain', certainty: 'when-known', from: 'The publisher domain' },
  { path: 'app.publisher.id', certainty: 'when-known', from: 'The publisher id' },
  { path: 'app.publisher.name', certainty: 'when-known', from: 'The publisher name' },
  { path: 'site.publisher.id', certainty: 'when-known', from: 'The publisher id' },
  { path: 'site.publisher.name', certainty: 'when-known', from: 'The publisher name' },

  // --- Content --------------------------------------------------------------------------
  { path: 'app.content.id', certainty: 'when-known', from: 'The content id' },
  { path: 'app.content.title', certainty: 'when-known', from: 'The content title' },
  { path: 'app.content.genre', certainty: 'when-known', from: 'The content genre' },
  { path: 'app.content.contentrating', certainty: 'when-known', from: 'The content rating' },
  { path: 'app.content.language', certainty: 'when-known', from: 'The content language' },
  { path: 'app.content.url', certainty: 'when-known', from: 'The content URL' },
  { path: 'app.content.len', certainty: 'when-known', from: 'The content length in seconds' },
  { path: 'app.content.series', certainty: 'when-known', from: 'The series name' },
  { path: 'app.content.season', certainty: 'when-known', from: 'The season' },
  { path: 'app.content.episode', certainty: 'when-known', from: 'The episode number' },
  { path: 'app.content.cat', certainty: 'when-known', from: 'The IAB content categories' },
  { path: 'app.content.keywords', certainty: 'when-known', from: 'The content keywords' },
  { path: 'app.content.channel.name', certainty: 'when-known', from: 'The channel' },
  { path: 'app.content.network.name', certainty: 'when-known', from: 'The network' },
  { path: 'app.content.livestream', certainty: 'when-known', from: 'Whether this is a live stream' },
  { path: 'site.content.id', certainty: 'when-known', from: 'The content id' },
  { path: 'site.content.title', certainty: 'when-known', from: 'The content title' },
  { path: 'site.content.genre', certainty: 'when-known', from: 'The content genre' },
  { path: 'site.content.contentrating', certainty: 'when-known', from: 'The content rating' },
  { path: 'site.content.language', certainty: 'when-known', from: 'The content language' },
  { path: 'site.content.url', certainty: 'when-known', from: 'The content URL' },
  { path: 'site.content.len', certainty: 'when-known', from: 'The content length in seconds' },
  { path: 'site.content.series', certainty: 'when-known', from: 'The series name' },
  { path: 'site.content.season', certainty: 'when-known', from: 'The season' },
  { path: 'site.content.episode', certainty: 'when-known', from: 'The episode number' },
  { path: 'site.content.cat', certainty: 'when-known', from: 'The IAB content categories' },
  { path: 'site.content.keywords', certainty: 'when-known', from: 'The content keywords' },
  { path: 'site.content.channel.name', certainty: 'when-known', from: 'The channel' },
  { path: 'site.content.network.name', certainty: 'when-known', from: 'The network' },
  { path: 'site.content.livestream', certainty: 'when-known', from: 'Whether this is a live stream' },

  // --- Privacy --------------------------------------------------------------------------
  // Forwarded, never invented: a position the publisher did not state is not one we assert.
  // **Both versions' locations** (#1315): OpenRTB 2.6 moved these out of `ext`, and the driver writes
  // the one the receiver's version reads. Listed rather than derived from `ORTB_FIELD_CATALOG`,
  // which imports this file; `ortb-merge-base.spec.ts` checks both against real requests.
  { path: 'regs.coppa', certainty: 'forwarded-if-sent', from: 'The COPPA flag the publisher sent' },
  { path: 'regs.gdpr', certainty: 'forwarded-if-sent', from: 'The GDPR flag the publisher sent' },
  { path: 'regs.ext.gdpr', certainty: 'forwarded-if-sent', from: 'The GDPR flag the publisher sent' },
  { path: 'regs.us_privacy', certainty: 'forwarded-if-sent', from: 'The CCPA string the publisher sent' },
  { path: 'regs.ext.us_privacy', certainty: 'forwarded-if-sent', from: 'The CCPA string the publisher sent' },
  { path: 'regs.gpp', certainty: 'forwarded-if-sent', from: 'The GPP string the publisher sent' },
  { path: 'regs.ext.gpp', certainty: 'forwarded-if-sent', from: 'The GPP string the publisher sent' },
  { path: 'regs.gpp_sid', certainty: 'forwarded-if-sent', from: 'The GPP section IDs the publisher sent' },
  { path: 'regs.ext.gpp_sid', certainty: 'forwarded-if-sent', from: 'The GPP section IDs the publisher sent' },
  { path: 'user.consent', certainty: 'forwarded-if-sent', from: 'The GDPR consent string the publisher sent' },
  { path: 'user.ext.consent', certainty: 'forwarded-if-sent', from: 'The GDPR consent string the publisher sent' },
  { path: 'user.eids', certainty: 'forwarded-if-sent', from: 'The extended user IDs the publisher sent' },
  { path: 'user.ext.eids', certainty: 'forwarded-if-sent', from: 'The extended user IDs the publisher sent' },
  { path: 'source.schain', certainty: 'forwarded-if-sent', from: 'The supply chain the publisher sent' },
  { path: 'source.ext.schain', certainty: 'forwarded-if-sent', from: 'The supply chain the publisher sent' },
];

/**
 * **Will this be populated without the operator authoring it?** — the question validation asks,
 * which is *not* the same as *does the driver build it*.
 *
 * A `forwarded-if-sent` field is built by the driver and may still be absent on every request,
 * because the publisher never sent one and we do not invent one. That is a gap worth telling the
 * operator about; a `when-known` device field is not, because authoring it can only override what
 * the session already supplies.
 */
export function isPopulatedWithoutAuthoring(field: OrtbMergeBaseField | undefined): boolean {
  return field !== undefined && field.certainty !== 'forwarded-if-sent';
}

/** Index by path — the console asks per leaf, once per render. */
const BY_PATH = new Map(ORTB_MERGE_BASE.map((f) => [f.path, f]));

/**
 * What the driver builds at a dotted path, or `undefined` where it builds nothing.
 *
 * Array indices are collapsed by the caller, or here: `imp[0].video.w` and `imp.video.w` are
 * the same question, and an operator reading a row label types whichever they see.
 *
 * **A concept answers under every spelling** (reported 2026-09-15). The placement concept is declared
 * once, at the name 2.6 sends (`imp.video.plcmt`), while the catalog addresses it as `video.placement`
 * and 2.5 sends `placement`. Looked up literally, validation asked about `video.placement`, found
 * nothing, and warned *"the driver won't add it"* beside a paste note saying the driver builds it —
 * two readers of one declaration disagreeing, with a Fix offering to add what is already sent. The
 * spellings come from the concept declaration, so a third version's name is covered by adding its row
 * there.
 */
export function mergeBaseFieldAt(path: string): OrtbMergeBaseField | undefined {
  const bare = path.replace(/\[\d+\]/g, '');
  const direct = BY_PATH.get(bare);
  if (direct) return direct;

  const segments = bare.split('.');
  const concept = protocolConceptForWireField(segments[segments.length - 1] ?? '');
  if (!concept || segments[segments.length - 2] !== concept.on) return undefined;
  const prefix = segments.slice(0, -1).join('.');
  for (const name of new Set([concept.concept, ...wireFieldNamesFor(concept.concept)])) {
    const declared = BY_PATH.get(`${prefix}.${name}`);
    if (declared) return declared;
  }
  return undefined;
}
